import { Building2, FileCheck, Landmark, ShieldAlert, Siren, Stethoscope, Users, Vote } from 'lucide-react';

/*
 * Content for the Government scenario walkthrough, drawn from the `government`
 * industry demo pack in the BOSSS repo.
 *
 * Nordvik County is a composite demo organization, not a real customer. Every
 * `artifact` string is a verbatim record title from the pack, read from the
 * domain YAML rather than from ORG.md's storyline contract.
 *
 * The framing rules this content must satisfy are enforced by scenarios.test.js.
 */

export const nordvikScenario = {
  org: {
    name: 'Nordvik County, Minnesota',
    subtitle: 'Five situations a county government actually faces, across independently elected offices.',
    framingNote: 'a composite organization in the BOSSS demo environment',
    profile: [
      { icon: Users, text: '~245,000 residents' },
      { icon: Building2, text: '~1,500 county employees' },
      { icon: Landmark, text: '~$310M annual budget' },
      { icon: Vote, text: '5 elected row offices' },
    ],
  },
  scenarios: [
    {
      id: 'county-ransomware',
      number: 1,
      title: 'Ransomware on County Systems',
      tagline: 'Permitting and land records encrypted, with no isolated backup to fall back on',
      icon: ShieldAlert,
      frameworks: ['CIS Controls v8', 'NIST CSF 2.0', 'MGDPA'],
      situation: {
        narrative: 'A phishing email reaches the permitting division, credentials are harvested through a cloned '
          + 'identity-provider page, and the attacker moves laterally over SMB. Ransomware encrypts the '
          + 'permitting and land-records file share. Recovery exposes a second problem: backup jobs are not '
          + 'isolated per department, so a clean restore is harder than it should be.',
        cast: [
          { name: 'Victor Nyquist', role: 'Chief Information Security Officer' },
          { name: 'Elena Marsh', role: 'Chief Information Officer' },
          { name: 'Jason Kessler', role: 'SOC / Security Analyst' },
          { name: 'Todd Ferraro', role: 'Emergency Management Director' },
        ],
      },
      timing: [
        { at: 'Day 0', label: 'Phishing email to permitting staff' },
        { at: 'Day 2', label: 'Lateral movement over SMB' },
        { at: 'Day 3', label: 'Mass encryption of the land-records share' },
        { at: 'Ongoing', label: 'Backup isolation gap tracked to closure' },
      ],
      response: [
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Encryption behaviour on the land-records share raises the flagship alert',
          artifact: 'Ransomware beacon / mass file-encryption on the permitting/land-records share',
        },
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Escalated to a full investigation case',
          artifact: 'Ransomware Investigation — Permitting/Land-Records File Share',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Formal incident record covering the encrypted county systems',
          artifact: 'Ransomware on County Systems — Permitting/Land-Records File Share',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The recovery weakness is recorded as its own finding',
          artifact: 'No Isolated Backup Segmentation for Per-Department Jobs (Ransomware Recovery Gap)',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'That finding becomes a tracked plan of action and milestones',
          artifact: 'POA&M — Per-Department Backup Isolation Gap (Ransomware Recovery)',
        },
        {
          module: 'Documents',
          href: '/platform/documents',
          action: 'The response runbook governs how the incident is worked',
          artifact: 'Ransomware Response Runbook',
        },
      ],
      enables: [
        'The incident and the recovery gap it exposed are separate records, so restoring service does not quietly close the weakness that made restoration hard.',
        'Emergency Management, IT and the CISO work one incident rather than three parallel accounts of it.',
      ],
      impact: {
        entities: [
          { name: 'Nordvik County', detail: 'Public Works, Recorder', location: 'Torvald, MN' },
        ],
        locations: [
          { name: 'Nordvik County Government Center', detail: 'Headquarters · land records, IT', location: 'Torvald, MN' },
          { name: 'Nordvik County Public Works & Highway', detail: 'Office · permitting', location: 'Torvald, MN' },
          { name: 'Nordvik County DR / COOP Site', detail: 'Disaster Recovery Site', location: 'Northbay, MN' },
        ],
        applications: [
          { name: 'County Permitting Portal', detail: 'Initial access path', location: 'Torvald, MN' },
          { name: 'Recorder\'s Land Records System', detail: 'Encrypted', location: 'Torvald, MN' },
          { name: 'Meridian Identity SSO', detail: 'Cloned login page', location: 'Torvald, MN' },
          { name: 'Granite Shield Endpoint (EDR Console)', location: 'Torvald, MN' },
        ],
        vendors: [
          { name: 'Meridian Identity Solutions', detail: 'SSO / identity provider', location: 'San Francisco, CA' },
          { name: 'Granite Shield Endpoint Security', detail: 'Endpoint detection', location: 'Austin, TX' },
          { name: 'Lodestar Security Analytics', detail: 'SIEM', location: 'Reston, VA' },
          { name: 'Cascadia Network Services', detail: 'WAN connectivity', location: 'Seattle, WA' },
        ],
        data: [
          { name: 'Recorded Deed/Title Record', detail: '~95,000 recorded instruments', location: 'Torvald, MN' },
          { name: 'Recorded Mortgage/Lien Record', detail: '~38,000 active mortgages/liens', location: 'Torvald, MN' },
          { name: 'Property Parcel Record', detail: '~95,000 taxable parcels', location: 'Torvald, MN' },
          { name: 'Survey Plat Map', detail: '~4,200 recorded plats', location: 'Torvald, MN' },
        ],
      },
    },
    {
      id: 'cjis-audit',
      number: 2,
      title: 'CJIS Audit Finding',
      tagline: 'Advanced authentication missing on criminal-justice system access',
      icon: Siren,
      frameworks: ['FBI CJIS Security Policy', 'NIST CSF 2.0'],
      situation: {
        narrative: 'Repeated failed advanced-authentication attempts against the Sheriff\'s CAD/RMS surface a '
          + 'compliance problem underneath: administrative access to criminal-justice systems does not fully '
          + 'meet the CJIS Security Policy advanced-authentication requirement. The Terminal Agency Coordinator '
          + 'and the CISO work it ahead of the periodic CJIS audit.',
        cast: [
          { name: 'Thomas Reinholt', role: 'Sheriff' },
          { name: 'Deputy Sarah Lindqvist', role: 'CJIS Terminal Agency Coordinator' },
          { name: 'Victor Nyquist', role: 'Chief Information Security Officer' },
          { name: 'Nathan Brekke', role: 'GRC Analyst' },
        ],
      },
      timing: [
        { at: 'Detected', label: 'Failed advanced-authentication cluster' },
        { at: 'Reviewed', label: 'Access anomaly worked as a case' },
        { at: 'Tracked', label: 'POA&M open to the audit date' },
      ],
      response: [
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Failed authentication against criminal-justice systems is detected',
          artifact: 'Repeated failed advanced-authentication to CAD/RMS (CJIS)',
        },
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Reviewed as a case rather than dismissed as noise',
          artifact: 'CJIS Access Anomaly Review — CAD/RMS Advanced Authentication',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The underlying policy gap is recorded against the authority',
          artifact: 'CJIS Security Policy — Advanced Authentication Gap on CAD/RMS Administrative Access',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'A plan of action and milestones carries it to the audit',
          artifact: 'POA&M — CJIS Advanced Authentication Gap on CAD/RMS Administrative Access',
        },
        {
          module: 'Documents',
          href: '/platform/documents',
          action: 'The county addendum to the federal policy governs local implementation',
          artifact: 'CJIS Security Policy Addendum',
        },
      ],
      enables: [
        'A detection and a compliance gap link to each other, so an auditor sees why the finding was raised rather than taking it on trust.',
        'The Sheriff\'s Office runs a constitutionally independent operation and still shares one compliance record with county IT.',
      ],
      impact: {
        entities: [
          { name: 'Nordvik County Sheriff\'s Office', detail: 'Elected row office', location: 'Torvald, MN' },
        ],
        locations: [
          { name: 'Nordvik County Law Enforcement Center', detail: 'Office · CAD/RMS, jail, dispatch', location: 'Torvald, MN' },
        ],
        applications: [
          { name: 'Sentinel CAD/RMS', detail: 'Advanced-authentication gap', location: 'Torvald, MN' },
          { name: 'Sentinel Jail Management Module', location: 'Torvald, MN' },
          { name: '911/PSAP Dispatch Console', location: 'Torvald, MN' },
          { name: 'Body-Worn Camera Evidence Management System', location: 'Torvald, MN' },
        ],
        vendors: [
          { name: 'Sentinel Dispatch Systems', detail: 'CAD/RMS platform', location: 'Lenexa, KS' },
          { name: 'Meridian Identity Solutions', detail: 'SSO / identity provider', location: 'San Francisco, CA' },
          { name: 'Cobblestone Assurance Group', detail: 'Independent assessor', location: 'Chicago, IL' },
        ],
        data: [
          { name: 'Criminal History Record Information (CHRI)', detail: '~3,200 CHRI queries/year', location: 'Torvald, MN' },
          { name: 'Active Investigation Case File', detail: '~640 open case files', location: 'Torvald, MN' },
          { name: 'Fingerprint/Biometric Identification Record', detail: '~2,100 bookings/year', location: 'Torvald, MN' },
        ],
      },
    },
    {
      id: 'election-security',
      number: 3,
      title: 'Securing the November Election',
      tagline: 'A spear-phish at elections staff, weeks before the vote',
      icon: Vote,
      frameworks: ['CIS Controls v8', 'NIST CSF 2.0', 'MGDPA'],
      situation: {
        narrative: 'An elections systems specialist is targeted by a spear-phish using a cloned Secretary of State '
          + 'login page, in the run-up to the November election. The attempt fails, but the review that follows '
          + 'surfaces a chain-of-custody logging gap on one precinct\'s tabulator — a documentation problem with '
          + 'real consequences for canvass confidence.',
        cast: [
          { name: 'Daniel Bergquist', role: 'Auditor-Treasurer, runs Elections' },
          { name: 'Teresa Nakamura', role: 'Elections Director' },
          { name: 'Brian Osei', role: 'Elections Systems Specialist' },
          { name: 'Victor Nyquist', role: 'Chief Information Security Officer' },
        ],
      },
      timing: [
        { at: 'Pre-election', label: 'Spear-phish against elections staff' },
        { at: 'Same week', label: 'Cloned state login page identified' },
        { at: 'Post-review', label: 'Tabulator custody gap found on one precinct' },
      ],
      response: [
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'The targeted phish is detected before credentials are used',
          artifact: 'Spear-phish targeting an elections-division staffer',
        },
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Worked as a pre-election credential-harvest case',
          artifact: 'Election-Staff Spear-Phish — Pre-Election Credential Harvest',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Formal incident record, so the attempt is on the books before the election',
          artifact: 'Spear-Phishing Attempt Against Elections Systems Specialist',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The review surfaces a custody-log gap on one precinct tabulator',
          artifact: 'Election — Tabulator Chain-of-Custody Log Gap for One Precinct',
        },
        {
          module: 'CMDB',
          href: '/platform/cmdb',
          action: 'Election equipment is tracked as inventory with a known location',
          artifact: 'BallotGuard Election Management System',
        },
      ],
      enables: [
        'A failed attack still produces a record, which is what lets the county show what happened rather than assert that nothing did.',
        'Election equipment, the precinct it serves and its custody log resolve to one inventory rather than a spreadsheet in the warehouse.',
      ],
      impact: {
        entities: [
          { name: 'Nordvik County Auditor-Treasurer', detail: 'Elections administration', location: 'Torvald, MN' },
        ],
        locations: [
          { name: 'Nordvik County Elections Warehouse', detail: 'Warehouse · tabulator storage', location: 'Torvald, MN' },
          { name: 'Nordvik County Government Center', detail: 'Headquarters · canvass and administration', location: 'Torvald, MN' },
          { name: 'South County Service Center', detail: 'Branch Office · absentee intake', location: 'Meadowridge, MN' },
        ],
        applications: [
          { name: 'BallotGuard Election Management System', detail: 'Phishing target', location: 'Torvald, MN' },
          { name: 'Meridian Identity SSO', detail: 'Cloned state login page', location: 'Torvald, MN' },
        ],
        vendors: [
          { name: 'BallotGuard Election Systems', detail: 'Election management platform', location: 'Omaha, NE' },
          { name: 'Lodestar Security Analytics', detail: 'SIEM', location: 'Reston, VA' },
        ],
        data: [
          { name: 'Voter Registration Record', detail: '~178,000 registered voters', location: 'Torvald, MN' },
          { name: 'Absentee/Mail Ballot Status', detail: '~42,000 absentee/mail ballots', location: 'Torvald, MN' },
          { name: 'Precinct Tabulator Results (Pre-Canvass)', detail: '~62 precincts', location: 'Torvald, MN' },
        ],
      },
    },
    {
      id: 'public-health-phi',
      number: 4,
      title: 'Public Health PHI Disclosure',
      tagline: 'An immunization dataset sent to the wrong recipient',
      icon: Stethoscope,
      frameworks: ['HIPAA', 'MGDPA', 'NIST CSF 2.0'],
      situation: {
        narrative: 'Data loss prevention catches an immunization dataset export leaving Public Health addressed to '
          + 'the wrong recipient. It is a disclosure of protected health information, and under Minnesota\'s '
          + 'Government Data Practices Act it is also a government-data incident — two regimes over one event, '
          + 'with the County Attorney\'s office as data-practices compliance authority.',
        cast: [
          { name: 'Dr. Anita Bergstrom', role: 'Public Health Director' },
          { name: 'Michael Chen', role: 'Public Health Informatics Analyst' },
          { name: 'Renee Dubois', role: 'Data Practices Compliance Official' },
          { name: 'Priya Venkataraman', role: 'County Attorney' },
        ],
      },
      timing: [
        { at: 'Detected', label: 'DLP flags the misdirected export' },
        { at: 'Same day', label: 'Disclosure case opened' },
        { at: 'Tracked', label: 'Finding worked under HIPAA and MGDPA' },
      ],
      response: [
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Data loss prevention catches the export before wider distribution',
          artifact: 'Misdirected immunization-dataset export detected by DLP',
        },
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Worked as a disclosure investigation',
          artifact: 'Misdirected Immunization Dataset — PHI Disclosure',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Formal incident record for the disclosure',
          artifact: 'Misdirected Immunization Dataset Disclosure — Public Health',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Recorded as a finding against both applicable regimes',
          artifact: 'Public Health — Misdirected Immunization Dataset (PHI Disclosure)',
        },
        {
          module: 'Documents',
          href: '/platform/documents',
          action: 'The safeguards procedure governs how PHI is handled county-side',
          artifact: 'HIPAA / PHI Safeguards Procedure',
        },
      ],
      enables: [
        'One disclosure is assessed against HIPAA and the state data-practices act together, instead of two separate reviews reaching two conclusions.',
        'The County Attorney\'s data-practices role is a named owner on the record, not an email thread after the fact.',
      ],
      impact: {
        entities: [
          { name: 'Nordvik County Public Health', detail: 'Health & Human Services', location: 'Torvald, MN' },
        ],
        locations: [
          { name: 'Nordvik County Health & Human Services Building', detail: 'Office · Public Health', location: 'Torvald, MN' },
        ],
        applications: [
          { name: 'VitalPoint Immunization / EHR-Lite Platform', detail: 'Export source', location: 'Torvald, MN' },
          { name: 'County Document Management System', location: 'Torvald, MN' },
        ],
        vendors: [
          { name: 'VitalPoint Health Records', detail: 'Immunization platform', location: 'Nashville, TN' },
          { name: 'Lodestar Security Analytics', detail: 'SIEM and DLP telemetry', location: 'Reston, VA' },
        ],
        data: [
          { name: 'Immunization History Record', detail: '~41,000 active records', location: 'Torvald, MN' },
          { name: 'Communicable Disease Case Report', detail: '~340 reports/year', location: 'Torvald, MN' },
          { name: 'Contact Tracing Log Entry', detail: '~1,100 entries/year', location: 'Torvald, MN' },
        ],
      },
    },
    {
      id: 'fti-vendor-risk',
      number: 5,
      title: 'Benefits Vendor Breach and FTI Risk',
      tagline: 'A cloud eligibility vendor is breached, and federal tax information is in scope',
      icon: FileCheck,
      frameworks: ['IRS Publication 1075', 'MGDPA', 'NIST CSF 2.0'],
      situation: {
        narrative: 'The cloud eligibility vendor used by Health & Human Services discloses a breach. Because '
          + 'benefits eligibility is verified against IRS income data, federal tax information falls within '
          + 'scope — and IRS Publication 1075 safeguards apply to the county as the recipient agency, not just '
          + 'to the vendor.',
        cast: [
          { name: 'Robert Sundqvist', role: 'HHS Director' },
          { name: 'Carlos Medina', role: 'FTI Data Steward' },
          { name: 'Denise Okoye', role: 'Benefits Eligibility Supervisor' },
          { name: 'Renee Dubois', role: 'Data Practices Compliance Official' },
        ],
      },
      timing: [
        { at: 'Disclosed', label: 'Vendor breach notification received' },
        { at: 'Matched', label: 'Breach indicators seen on the FTI-adjacent gateway' },
        { at: 'Tracked', label: 'Safeguard gap open against Publication 1075' },
      ],
      response: [
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Vendor breach indicators are matched against county traffic',
          artifact: 'Benefaxis breach IOC match on FTI-adjacent gateway',
        },
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Worked as a safeguard-review case',
          artifact: 'Benefits Vendor Breach — FTI Safeguard Review',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Formal incident record for the third-party exposure',
          artifact: 'Benefaxis Eligibility Cloud FTI Vendor Breach Notification',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The safeguard gap the breach surfaced becomes a tracked finding',
          artifact: 'Benefaxis Eligibility Cloud — FTI Safeguard Gap Surfaced by Vendor Breach Disclosure',
        },
        {
          module: 'Documents',
          href: '/platform/documents',
          action: 'The controlling federal safeguard procedure governs the response',
          artifact: 'IRS Publication 1075 Safeguard Procedure',
        },
      ],
      enables: [
        'A vendor breach is assessed against the federal safeguard obligation the county itself carries, rather than treated as the vendor\'s problem alone.',
        'Benefits data, tax-verification data and the vendor that touches both resolve to one scope rather than three inventories.',
      ],
      impact: {
        entities: [
          { name: 'Nordvik County Health & Human Services', detail: 'Benefits eligibility', location: 'Torvald, MN' },
          { name: 'Benefaxis Eligibility Cloud', detail: 'Vendor — breached', location: 'Raleigh, NC' },
        ],
        locations: [
          { name: 'Nordvik County Health & Human Services Building', detail: 'Office · eligibility operations', location: 'Torvald, MN' },
          { name: 'Benefaxis Eligibility Cloud Headquarters', detail: 'Vendor site — breached', location: 'Raleigh, NC' },
        ],
        applications: [
          { name: 'Benefaxis Eligibility Cloud Portal', detail: 'Breached vendor platform', location: 'Torvald, MN' },
          { name: 'Benefits Eligibility Fraud-Detection Model', location: 'Torvald, MN' },
        ],
        vendors: [
          { name: 'Benefaxis Eligibility Cloud', detail: 'Breached — eligibility platform', location: 'Raleigh, NC' },
          { name: 'Cobblestone Assurance Group', detail: 'Independent assessor', location: 'Chicago, IL' },
        ],
        data: [
          { name: 'IRS Income Verification (FTI)', detail: '~5,100 matches/year', location: 'Torvald, MN' },
          { name: 'Benefits Eligibility Determination', detail: '~8,600 active case files', location: 'Torvald, MN' },
          { name: 'Benefits Applicant Household Income', detail: '~8,600 active case files', location: 'Torvald, MN' },
        ],
      },
    },
  ],
};
